/**
 * Время во всей системе хранится и передаётся в UTC (ISO 8601).
 * Форматирование в местный часовой пояс — задача слоя отображения:
 * у школы один часовой пояс, он задаётся в настройках и приходит с профилем.
 */
export const MINUTE_MS = 60_000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;
/**
 * Пересекаются ли интервалы. Касание границами (конец одного = начало другого)
 * пересечением не считается — уроки могут идти вплотную.
 */
export function overlaps(a, b) {
    return a.startsAt.getTime() < b.endsAt.getTime() && b.startsAt.getTime() < a.endsAt.getTime();
}
export function durationMinutes(interval) {
    return Math.round((interval.endsAt.getTime() - interval.startsAt.getTime()) / MINUTE_MS);
}
/** Сколько всего минут в списке интервалов. */
export function totalMinutes(intervals) {
    return intervals.reduce((sum, item) => sum + durationMinutes(item), 0);
}
export function addMinutes(date, minutes) {
    return new Date(date.getTime() + minutes * MINUTE_MS);
}
export function addDays(date, days) {
    return new Date(date.getTime() + days * DAY_MS);
}
/** Начало недели (понедельник, 00:00) в указанном часовом поясе. */
export function startOfWeek(date, timeZone) {
    const local = zonedParts(date, timeZone);
    // getUTCDay: 0 — воскресенье, приводим к понедельнику как началу недели
    const weekday = (local.weekday + 6) % 7;
    const midnight = zonedMidnight(date, timeZone);
    return addDays(midnight, -weekday);
}
/** Полночь указанной даты в указанном часовом поясе, как момент времени UTC. */
export function zonedMidnight(date, timeZone) {
    const { year, month, day } = zonedParts(date, timeZone);
    const offset = zoneOffsetMs(date, timeZone);
    return new Date(Date.UTC(year, month - 1, day) - offset);
}
/** Смещение часового пояса относительно UTC в миллисекундах для данного момента. */
export function zoneOffsetMs(date, timeZone) {
    const parts = zonedParts(date, timeZone);
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    // Секунды исходной даты обнуляем — Intl не отдаёт миллисекунды
    return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}
const WEEKDAY_INDEX = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};
/** Разбор момента времени на календарные поля в указанном часовом поясе. */
export function zonedParts(date, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        weekday: 'short',
    });
    const parts = {};
    for (const part of formatter.formatToParts(date)) {
        if (part.type !== 'literal')
            parts[part.type] = part.value;
    }
    return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        // В en-US полночь форматируется как 24, приводим к 0
        hour: Number(parts.hour) % 24,
        minute: Number(parts.minute),
        second: Number(parts.second),
        weekday: WEEKDAY_INDEX[parts.weekday ?? 'Mon'] ?? 1,
    };
}
/** Ключ дня «2026-08-03» в указанном часовом поясе — для группировки расписания. */
export function dayKey(date, timeZone) {
    const { year, month, day } = zonedParts(date, timeZone);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/** «10:30» → 630. */
export function timeToMinutes(value) {
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
}
/** 630 → «10:30». */
export function minutesToTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}
/**
 * Вычитает занятые интервалы из свободных. Так считается свободное время
 * инструктора: рабочие часы дня минус уже занятые куски.
 *
 * Списки сортируются внутри — снаружи порядок гарантировать неудобно,
 * а ошибка в нём молча дала бы лишнее «свободное» время.
 */
export function subtractIntervals(base, busy) {
    const taken = [...busy].sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
    const result = [];
    for (const interval of [...base].sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())) {
        let cursor = interval.startsAt.getTime();
        const end = interval.endsAt.getTime();
        for (const block of taken) {
            const blockStart = block.startsAt.getTime();
            const blockEnd = block.endsAt.getTime();
            if (blockEnd <= cursor)
                continue;
            if (blockStart >= end)
                break;
            if (blockStart > cursor) {
                result.push({ startsAt: new Date(cursor), endsAt: new Date(Math.min(blockStart, end)) });
            }
            cursor = Math.max(cursor, blockEnd);
            if (cursor >= end)
                break;
        }
        if (cursor < end)
            result.push({ startsAt: new Date(cursor), endsAt: new Date(end) });
    }
    return result;
}
/**
 * Возможные моменты начала занятия внутри свободного времени.
 *
 * Шаг отсчитывается от начала каждого свободного куска, а не от круглого часа:
 * если предыдущее занятие кончилось в 13:30, следующее можно начать в 13:30,
 * а не ждать 14:00. Начало предлагается, только если самое короткое занятие
 * успевает закончиться до конца куска.
 *
 * Живёт в общем пакете: этим же списком пользуются сайт, бот и проверка на
 * сервере — расчёт врозь рано или поздно разошёлся бы, и ученик получал бы
 * отказ по времени, которое ему только что предложили.
 */
export function bookingStartOptions(params) {
    const floor = params.notBefore?.getTime() ?? 0;
    const starts = [];
    for (const interval of params.free) {
        const end = interval.endsAt.getTime();
        let cursor = interval.startsAt.getTime();
        while (cursor + params.minMinutes * MINUTE_MS <= end) {
            if (cursor >= floor)
                starts.push(new Date(cursor));
            cursor += params.stepMinutes * MINUTE_MS;
        }
    }
    return starts.sort((a, b) => a.getTime() - b.getTime());
}
/**
 * Длительности, которые помещаются от выбранного начала до конца свободного
 * куска. Ученику показываются только они: предложить три часа там, где
 * свободен один, значит пообещать то, чего нет.
 */
export function fittingDurations(free, startsAt, options) {
    const start = startsAt.getTime();
    const holder = free.find((interval) => interval.startsAt.getTime() <= start && start < interval.endsAt.getTime());
    if (!holder)
        return [];
    const available = Math.floor((holder.endsAt.getTime() - start) / MINUTE_MS);
    return options.filter((minutes) => minutes <= available);
}
/** Помещается ли занятие целиком в один свободный кусок. */
export function fitsInFreeTime(free, interval) {
    return free.some((item) => item.startsAt.getTime() <= interval.startsAt.getTime() &&
        item.endsAt.getTime() >= interval.endsAt.getTime());
}
/**
 * Собирает момент времени из локальных даты и времени в указанном поясе.
 * `dateStr` — «2026-08-03», `timeStr` — «14:30».
 */
export function fromZonedDateTime(dateStr, timeStr, timeZone) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hour, minute] = timeStr.split(':').map(Number);
    // Первое приближение — считаем ввод как UTC, затем корректируем на смещение пояса.
    const naive = Date.UTC(year, month - 1, day, hour, minute);
    const offset = zoneOffsetMs(new Date(naive), timeZone);
    const corrected = new Date(naive - offset);
    // Повторная корректировка ловит переход на летнее время рядом с границей.
    const settledOffset = zoneOffsetMs(corrected, timeZone);
    return settledOffset === offset ? corrected : new Date(naive - settledOffset);
}
//# sourceMappingURL=time.js.map